import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2, BarChart3, Calendar as CalendarIcon, Users as UsersIcon, Phone,
  TrendingUp, Plus, Save, Trash2, Edit2,
} from 'lucide-react';

/**
 * Daily Call Report (Telemarketing KPI dashboard).
 *
 * Renders three rollups inside an optional [date_from, date_to] window:
 *   • KPI cards (calls / contacted / appointments / conversion %)
 *   • Per-agent table (conversion % per telemarketer)
 *   • Per-day table (most recent 30 days)
 *
 * Below the rollups: a CRUD-able list of legacy "Daily Call Report" sheets
 * captured by each operator (mirror of the Access `DailyCallReportTable`).
 */
export default function TelemarketingKpiDashboard() {
  const { api } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      const { data } = await api.get(`/telemarketing/kpi-stats?${qs.toString()}`);
      setKpi(data);
    } catch { setKpi(null); }
    setLoading(false);
  }, [api, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ agent_name: '', report_date: today, product_offered: 'Free Appraisal', total_calls: 0, contacted: 0, requested_info: 0, scheduled_followup: 0, not_interested: 0, no_budget: 0, appointments: 0, other: 0, comments: '' });
    setEditing('new');
  };

  const startEdit = (r) => {
    setForm({ ...r });
    setEditing(r._id);
  };

  const cancelEdit = () => {
    setForm({});
    setEditing(null);
  };

  const saveReport = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload._id;
      delete payload.created_at;
      delete payload.updated_at;
      ['total_calls', 'contacted', 'requested_info', 'scheduled_followup', 'not_interested', 'no_budget', 'appointments', 'other'].forEach(k => {
        payload[k] = parseInt(payload[k], 10) || 0;
      });
      if (editing && editing !== 'new') {
        await api.put(`/telemarketing/daily-reports/${editing}`, payload);
      } else {
        await api.post('/telemarketing/daily-reports', payload);
      }
      cancelEdit();
      load();
    } catch { /* noop */ }
    setSaving(false);
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this daily report?')) return;
    await api.delete(`/telemarketing/daily-reports/${id}`);
    load();
  };

  const totals = kpi?.totals || {};
  const byAgent = kpi?.by_agent || [];
  const byDay = kpi?.by_day || [];
  const dailyReports = kpi?.daily_reports || [];

  const maxAgentCalls = useMemo(() => Math.max(1, ...byAgent.map(a => a.calls || 0)), [byAgent]);

  if (loading) {
    return <div data-testid="tm-kpi-loading" className="bg-white border border-slate-200 rounded-xl p-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading KPIs…</div>;
  }

  return (
    <div data-testid="tm-kpi-dashboard" className="space-y-4">
      {/* Date filter */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><CalendarIcon size={12} /> Period</span>
        <input data-testid="tm-kpi-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-xs" />
        <span className="text-slate-400 text-xs">→</span>
        <input data-testid="tm-kpi-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1 border border-slate-200 rounded text-xs" />
        <button data-testid="tm-kpi-quick-today" onClick={() => { setDateFrom(today); setDateTo(today); }} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Today</button>
        <button data-testid="tm-kpi-quick-7" onClick={() => { setDateFrom(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)); setDateTo(today); }} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Last 7d</button>
        <button data-testid="tm-kpi-quick-30" onClick={() => { setDateFrom(thirtyAgo); setDateTo(today); }} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Last 30d</button>
        <button data-testid="tm-kpi-quick-all" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[10px] px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">All time</button>
        <span className="ml-auto text-[10px] text-slate-400">Source: real-time calls collection + daily-report sheets</span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="tm-kpi-calls" label="Calls logged" value={totals.calls || 0} icon={<Phone size={14} />} tint="orange" />
        <KpiCard testid="tm-kpi-bookings" label="Appointments" value={totals.bookings || 0} icon={<TrendingUp size={14} />} tint="emerald" />
        <KpiCard testid="tm-kpi-conversion" label="Conversion" value={`${(totals.conversion_pct || 0).toFixed(1)}%`} icon={<BarChart3 size={14} />} tint="blue" />
        <KpiCard testid="tm-kpi-followups" label="Pending follow-ups" value={totals.pending_follow_ups || 0} icon={<UsersIcon size={14} />} tint="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {totals.calls === 0 && byAgent.length === 0 && byDay.length === 0 ? (
          <div data-testid="tm-kpi-empty-state" className="lg:col-span-2 bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center space-y-2">
            <Phone size={26} className="mx-auto text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">No telemarketing calls logged in this window yet.</p>
            <p className="text-xs text-slate-400">Once your team starts logging calls in <strong>Call Workspace</strong> (or submitting <em>Daily Reports</em> below), agent + day rollups will populate here.</p>
          </div>
        ) : (
        <>
        {/* By agent */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <header className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <UsersIcon size={13} className="text-[#D35400]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">By telemarketer</h3>
          </header>
          {byAgent.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-slate-400">No telemarketer activity in this window.</p>
          ) : (
            <table data-testid="tm-kpi-by-agent" className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Agent</th>
                  <th className="text-right px-3 py-2">Calls</th>
                  <th className="text-right px-3 py-2">Appts</th>
                  <th className="text-right px-3 py-2">Conv %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {byAgent.map(a => (
                  <tr key={a.agent} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-slate-700">{a.agent}</td>
                    <td className="px-3 py-2 text-right font-mono-num">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden hidden md:block">
                          <div className="h-full bg-[#D35400]" style={{ width: `${(a.calls / maxAgentCalls) * 100}%` }} />
                        </div>
                        {a.calls}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-semibold">{a.appointments}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{a.conversion_pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* By day */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <header className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <CalendarIcon size={13} className="text-[#D35400]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">By day (most recent 30)</h3>
          </header>
          {byDay.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-slate-400">No call activity in this window.</p>
          ) : (
            <table data-testid="tm-kpi-by-day" className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Calls</th>
                  <th className="text-right px-3 py-2">Appts</th>
                  <th className="text-right px-3 py-2">Follow-up</th>
                  <th className="text-right px-3 py-2">Conv %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {byDay.map(d => (
                  <tr key={d.date} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono-num text-slate-600">{d.date}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{d.calls}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{d.appointments}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-blue-700">{d.follow_up}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{d.conversion_pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        </>
        )}
      </div>

      {/* Daily Call Report sheets (legacy MS Access) */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <BarChart3 size={13} className="text-[#D35400]" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">Daily call reports</h3>
          <span className="text-[10px] text-slate-400">— legacy operator sheet (end-of-day rollup)</span>
          <button data-testid="tm-kpi-new-report" onClick={startNew} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#D35400] hover:bg-[#A04000] text-white text-xs font-semibold">
            <Plus size={12} /> New report
          </button>
        </header>

        {editing && (
          <div data-testid="tm-kpi-report-form" className="bg-amber-50/30 border-b border-amber-100 px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Field label="Date"><input type="date" data-testid="tm-kpi-form-date" value={form.report_date || ''} onChange={e => setForm({ ...form, report_date: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
              <Field label="Agent"><input data-testid="tm-kpi-form-agent" value={form.agent_name || ''} onChange={e => setForm({ ...form, agent_name: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
              <Field label="Product offered" wide><input data-testid="tm-kpi-form-product" value={form.product_offered || ''} onChange={e => setForm({ ...form, product_offered: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" /></Field>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-2">
              {[
                ['total_calls', 'Calls'], ['contacted', 'Contacted'], ['requested_info', 'Info'],
                ['scheduled_followup', 'Follow-up'], ['not_interested', 'Not int.'], ['no_budget', 'No $$'],
                ['appointments', 'Appts'], ['other', 'Other'],
              ].map(([k, label]) => (
                <Field key={k} label={label}>
                  <input
                    type="number" min={0}
                    data-testid={`tm-kpi-form-${k.replace(/_/g, '-')}`}
                    value={form[k] ?? 0}
                    onChange={e => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs font-mono-num text-right"
                  />
                </Field>
              ))}
            </div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-2 mb-1">Comments</label>
            <textarea data-testid="tm-kpi-form-comments" value={form.comments || ''} onChange={e => setForm({ ...form, comments: e.target.value })} rows={2} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={cancelEdit} className="px-3 py-1.5 border border-slate-200 rounded text-xs">Cancel</button>
              <button data-testid="tm-kpi-form-save" onClick={saveReport} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold disabled:opacity-40">
                <Save size={12} /> Save report
              </button>
            </div>
          </div>
        )}

        {dailyReports.length === 0 ? (
          <p className="px-4 py-8 text-center text-xs text-slate-400">No daily reports captured for this window.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-right px-3 py-2">Calls</th>
                <th className="text-right px-3 py-2">Contacted</th>
                <th className="text-right px-3 py-2">Appts</th>
                <th className="text-right px-3 py-2">Follow-up</th>
                <th className="text-right px-3 py-2">Not int.</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dailyReports.map(r => (
                <tr key={r._id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.report_date || '—'}</td>
                  <td className="px-3 py-2 font-semibold">{r.agent_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{r.product_offered || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.total_calls || 0}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.contacted || 0}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-semibold">{r.appointments || 0}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.scheduled_followup || 0}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.not_interested || 0}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => startEdit(r)} className="p-1 text-slate-400 hover:text-[#D35400]" title="Edit"><Edit2 size={11} /></button>
                    <button onClick={() => deleteReport(r._id)} className="p-1 text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={11} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

const Field = ({ label, children, wide }) => (
  <div className={wide ? 'col-span-2 md:col-span-2' : ''}>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const KpiCard = ({ label, value, icon, tint, testid }) => {
  const tints = {
    orange: 'from-orange-50 to-orange-100/30 text-orange-700 ring-orange-200',
    emerald: 'from-emerald-50 to-emerald-100/30 text-emerald-700 ring-emerald-200',
    blue: 'from-blue-50 to-blue-100/30 text-blue-700 ring-blue-200',
    amber: 'from-amber-50 to-amber-100/30 text-amber-700 ring-amber-200',
  };
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${tints[tint] || tints.orange} ring-1 rounded-xl p-3.5`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-extrabold font-[Manrope] tabular-nums tracking-tight">{value}</div>
    </div>
  );
};
